/**
 * Word Labyrinth — grammatically joined sentence pairs (specs/labyrinth-mode.md §6.4 grade 2).
 *
 * Some sets are bare noun phrases with no `vorgabe` (`der große Hund`), and others are predicates
 * whose `vorgabe` names the same noun (`[Der Hund]` + `frisst sein Futter`). Glue the first in
 * front of the second's tiles and one long grammatical sentence falls out:
 *
 *     der große Hund  +  frisst sein Futter  →  Der große Hund frisst sein Futter.
 *
 * **This is a garnish and can never be the engine.** Measured against the shipped packs the yield
 * is 6–10 pairs per Latin locale and **zero in ja/ko/th** — those three have no bare noun-phrase
 * sets at all, since every one of their sentences already carries its subject in the `vorgabe`. So
 * every caller must be able to fall back to a plain chain, and `join.test.ts` pins that the three
 * empty locales return `[]` rather than throwing.
 */
import type { SentenceSet } from './sets';

export interface JoinedPair {
  np: SentenceSet;
  vp: SentenceSet;
}

const tokens = (s: string): string[] =>
  s.split(/\s+/).map((t) => t.replace(/[.,!?;:]/gu, '').toLowerCase()).filter(Boolean);

/**
 * Whether a noun phrase can stand in for a predicate's `vorgabe`.
 *
 * The test is that some tile of the noun phrase **other than its first** also appears as a token of
 * the predicate's `vorgabe`. Skipping the first tile is what makes this work across word orders
 * without a stopword list: the opening tile is the article or demonstrative (`der`, `el`, `this`),
 * which appears in every `vorgabe` in the pack and would match everything. What is left is the noun
 * and its adjective, and the noun is the word the two halves have to agree on — `Hund` in
 * `[Der Hund]`, `gato` in `[El gato]`, `dog` in `[The dog]`, whichever side of the noun the
 * language puts its adjective on.
 */
function agrees(np: SentenceSet, vp: SentenceSet): boolean {
  if (!vp.vorgabe || np.vorgabe) return false;
  const subject = new Set(tokens(vp.vorgabe));
  return np.tiles.slice(1).some((t) => subject.has(t.toLowerCase()));
}

/** Every joinable pair in a pack — derived, never hand-written, so it cannot drift from the data. */
export function joinablePairs(sets: SentenceSet[]): JoinedPair[] {
  const nps = sets.filter((s) => !s.vorgabe);
  const vps = sets.filter((s) => s.vorgabe);
  const out: JoinedPair[] = [];
  for (const np of nps) {
    const vp = vps.find((v) => agrees(np, v));
    if (vp) out.push({ np, vp });
  }
  return out;
}

/**
 * The cells of a joined path: the noun phrase's tiles, then the predicate's, with the predicate's
 * own `vorgabe` dropped — the noun phrase has replaced it.
 */
export function joinedCells(pair: JoinedPair, lang: string): string[] {
  const cells = [...pair.np.tiles, ...pair.vp.tiles];
  cells[0] = capitalizeFirst(cells[0], lang);
  return cells;
}

/**
 * Upper-case the first grapheme of a sentence's opening cell.
 *
 * A joined sentence starts on a tile authored lower-case (`der`), because in its own set that tile
 * was never the first word. The seven Latin locales need it capitalised; ja/ko/th must be left
 * alone. The test is the *text*, not a locale allow-list — a script with no case maps to itself
 * under both `toUpperCase` and `toLowerCase`, so `この` and `หมา` fall through untouched and no
 * list has to be kept in sync with the shipped languages.
 */
export function capitalizeFirst(text: string, lang: string): string {
  const chars = [...text];
  if (chars.length === 0) return text;
  const first = chars[0];
  if (first.toUpperCase() === first.toLowerCase()) return text;
  return first.toLocaleUpperCase(lang) + chars.slice(1).join('');
}
